import React, { useEffect, useRef, useState } from "react";
import { motion, useInView, animate } from "framer-motion";
import { NUMBERS } from "../data/content";

function AnimatedNumber({ value, prefix = "", suffix = "" }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-40px" });
  const [display, setDisplay] = useState(0);

  useEffect(() => {
    if (!inView) return;
    const controls = animate(0, value, {
      duration: 1.8,
      ease: [0.16, 1, 0.3, 1],
      onUpdate: (v) => setDisplay(Math.round(v)),
    });
    return () => controls.stop();
  }, [inView, value]);

  return (
    <span ref={ref}>
      {prefix}
      {display.toLocaleString()}
      {suffix}
    </span>
  );
}

export default function Numbers() {
  return (
    <section className="vw-section relative bg-[var(--vw-bg-soft)] overflow-hidden" data-testid="vw-numbers">
      {/* Ambient orbs */}
      <div className="pointer-events-none absolute -top-20 -left-20 w-[520px] h-[520px] rounded-full opacity-20"
           style={{ background: "radial-gradient(circle, var(--vw-cyan), transparent 60%)" }} />
      <div className="pointer-events-none absolute -bottom-32 -right-24 w-[600px] h-[600px] rounded-full opacity-15"
           style={{ background: "radial-gradient(circle, var(--vw-blue), transparent 60%)" }} />

      <div className="vw-container relative">
        <div className="mb-14 md:mb-20 max-w-3xl">
          <div className="vw-eyebrow mb-6">BY THE NUMBERS</div>
          <h2 className="font-display text-5xl md:text-6xl lg:text-7xl leading-[0.92] tracking-tight">
            The math <span className="font-serif-italic font-normal text-[var(--vw-cyan)]">changes</span> when the pipeline changes.
          </h2>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-[var(--vw-border)]">
          {NUMBERS.map((n, i) => (
            <motion.div
              key={n.label}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.55, delay: i * 0.1 }}
              className="bg-[var(--vw-bg-soft)] p-8 md:p-10 relative group"
              data-testid={`vw-number-${i}`}
            >
              <span className="absolute top-0 left-0 h-px w-0 bg-[var(--vw-cyan)] group-hover:w-full transition-all duration-700" />
              <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-[var(--vw-text-mute)] mb-4">
                {String(i + 1).padStart(2, "0")} / {String(NUMBERS.length).padStart(2, "0")}
              </div>
              <div className="font-display text-[15vw] sm:text-[8vw] lg:text-[5.5vw] leading-none tracking-tighter vw-text-gradient">
                <AnimatedNumber value={n.value} prefix={n.prefix} suffix={n.suffix} />
              </div>
              <div className="mt-4 font-display text-lg font-semibold text-[var(--vw-text)]">
                {n.label}
              </div>
              <div className="mt-2 text-sm text-[var(--vw-text-dim)] leading-relaxed max-w-[26ch]">
                {n.note}
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
